import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { isErrorUserPendingApproval, isForbiddenError, storableError } from '../../util/errors';
import { convertUnitToSubUnit, unitDivisor } from '../../util/currency';
import {
  parseDateFromISO8601,
  getExclusiveEndDate,
  addTime,
  subtractTime,
  getStartOf,
} from '../../util/dates';
import { constructQueryParamName, isOriginInUse, MAX_SLIDER_IMAGES } from '../../util/search';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import { omitUiOnlyParams, parse } from '../../util/urlHelpers';
import { LOUNGE_MATCHER } from '../../util/airportLoungeConfig';
import { getReferralParams } from '../../util/webStorageHelpers';
import { apiPriceParam, isUnifiedSearchEnabled } from '../../util/unifiedSearch';
import { isHonestPlacesActive, placesApply, placesCount } from '../../util/searchPanelHonesty';
import { calendarFreeSearchParams } from '../../util/calendarFreeSearch';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { track } from '../../util/analytics';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 12 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
export const RESULT_PAGE_SIZE = 24;

// ================ Helper Functions ================ //

const resultIds = data => {
  const listings = data.data;
  return listings
    .filter(l => !l.attributes.deleted && l.attributes.state === 'published')
    .map(l => l.id);
};

// ================ Async Thunks ================ //

/////////////////////
// Search Listings //
/////////////////////
/**
 * Turns the search page's params (URL shape: `price=10,50`, `dates=…`, `pub_*`) into the params
 * listings/query takes. Shared by the result list and by the filter dialog's result count, so the
 * number on "N Angebote anzeigen" is the number the list will show after applying.
 *
 * @param {Object} searchParams parsed search params plus query options (perPage, include, fields)
 * @param {Object} config the app config
 * @returns {Object} params for sdk.listings.query
 */
export const buildListingsQueryParams = (searchParams, config) => {
  // SearchPage can enforce listing query to only those listings with valid listingType
  // NOTE: this only works if you have set 'enum' type search schema to listing's public data fields
  //       - listingType
  //       Same setup could be expanded to 2 other extended data fields:
  //       - transactionProcessAlias
  //       - unitType
  //       ...and then turned enforceValidListingType config to true in configListing.js
  // Read More:
  // https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
  const searchValidListingTypes = (listingTypes, listingTypePathParam, isListingTypeVariant) => {
    return isListingTypeVariant
      ? {
          pub_listingType: listingTypePathParam,
        }
      : config.listing.enforceValidListingType
      ? {
          pub_listingType: listingTypes.map(l => l.listingType),
          // pub_transactionProcessAlias: listingTypes.map(l => l.transactionType.alias),
          // pub_unitType: listingTypes.map(l => l.transactionType.unitType),
        }
      : {};
  };

  const constructCategoryPropertiesForAPI = (queryParamPrefix, categories, level, params) => {
    const levelKey = `${queryParamPrefix}${level}`;
    const levelValue =
      typeof params?.[levelKey] !== 'undefined' ? `${params?.[levelKey]}` : undefined;
    const foundCategory = categories.find(cat => cat.id === levelValue);
    const subcategories = foundCategory?.subcategories || [];
    // Note: we might need to prepare nested categories too: categoryLevel1, categoryLevel2, categoryLevel3
    return foundCategory && subcategories.length > 0
      ? {
          [levelKey]: levelValue,
          ...constructCategoryPropertiesForAPI(queryParamPrefix, subcategories, level + 1, params),
        }
      : foundCategory
      ? { [levelKey]: levelValue }
      : {};
  };

  /**
   * Category filter params are prepared here. We omit invalid category names.
   * I.e. params that are not part of the currently configured category tree.
   *
   * @param {string} paramName - The name of the parameter to prepare.
   * @param {Object} params - The search params object.
   * @returns {Object} The prepared parameter object.
   */
  const prepareCategoryParams = (paramName, params) => {
    const categoryConfig = config.search.defaultFilters?.find(f => f.schemaType === 'category');
    const categories = config.categoryConfiguration.categories;
    const { key, scope } = categoryConfig || {};
    const categoryParamPrefix = constructQueryParamName(key, scope);
    return paramName.startsWith(categoryParamPrefix)
      ? constructCategoryPropertiesForAPI(categoryParamPrefix, categories, 1, params)
      : {};
  };

  const constructIntegerRangePropertyForAPI = (queryParamPrefix, params) => {
    const integerValue = params?.[queryParamPrefix];
    const [min, max] = integerValue ? integerValue.split(',') : [];
    // NOTE: long filter needs exclusive max value on API side
    const inclusiveMin = Number.parseInt(min, 10);
    const exclusiveMax = Number.parseInt(max, 10) + 1;

    // NOTE: currently we don't validate the range values against the integer range config,
    // but we might want to do that in the future.

    return Number.isInteger(inclusiveMin) && Number.isInteger(exclusiveMax)
      ? { [queryParamPrefix]: [inclusiveMin, exclusiveMax].join(',') }
      : {};
  };

  /**
   * Integer range filter values are converted to API params of type 'long'.
   *
   * The range end must be exclusive. E.g. 1000,2000 -> 1000,2001
   *
   * NOTE: currently we don't validate the range values against the integer range config,
   * but we might want to do that in the future.
   *
   * @param {string} paramName - The name of the parameter to prepare.
   * @param {Object} params - The search params object.
   * @returns {Object} The prepared parameter object.
   */
  const prepareIntegerRangeParam = (paramName, params) => {
    const integerRangeConfig = config.listing.listingFields?.find(f => {
      const isInteger = f.schemaType === 'long';
      const isCorrectExtendedDataKey = constructQueryParamName(f.key, f.scope) === paramName;
      return isInteger && isCorrectExtendedDataKey;
    });
    const { key, scope } = integerRangeConfig || {};
    const integerParamPrefix = constructQueryParamName(key, scope);
    return paramName.startsWith(integerParamPrefix)
      ? constructIntegerRangePropertyForAPI(integerParamPrefix, params)
      : {};
  };

  // This function goes through given params and if there's a specific handler for the parameter type,
  // it calls the handler to prepare the property for API.
  // Otherwise, it just passes the param through.
  const prepareAPIParams = (params, paramHandlers) => {
    const pickedKeys = Object.entries(params).reduce((picked, [k, v]) => {
      const preparedParams = paramHandlers.reduce((picked, fn) => {
        return { ...picked, ...fn(k, params) };
      }, {});

      // If the param is not handled by any of the handlers, we pass it through.
      const currentParam = Object.keys(preparedParams).length > 0 ? preparedParams : { [k]: v };

      return { ...picked, ...currentParam };
    }, {});

    return pickedKeys;
  };

  const priceSearchParams = priceParam => {
    // Unified search: a max at the configured maximum is open-ended (`min,`). The hosted maximum
    // is CHF 500 and 32 of 129 spaces cost more — the closed range dropped them all the moment
    // the slider was touched (util/unifiedSearch.js apiPriceParam).
    if (isUnifiedSearchEnabled()) {
      const apiPrice = apiPriceParam(priceParam, config);
      return apiPrice ? { price: apiPrice } : {};
    }
    const inSubunits = value => convertUnitToSubUnit(value, unitDivisor(config.currency));
    const values = priceParam ? priceParam.split(',') : [];
    return priceParam && values.length === 2
      ? {
          price: [inSubunits(values[0]), inSubunits(values[1]) + 1].join(','),
        }
      : {};
  };

  const datesSearchParams = datesParam => {
    const searchTZ = 'Etc/UTC';
    const datesFilter = config.search.defaultFilters.find(f => f.key === 'dates');
    const values = datesParam ? datesParam.split(',') : [];
    const hasValues = datesFilter && datesParam && values.length === 2;
    const { dateRangeMode, availability } = datesFilter || {};
    const isNightlyMode = dateRangeMode === 'night';
    const isEntireRangeAvailable = availability === 'time-full';

    // SearchPage need to use a single time zone but listings can have different time zones
    // We need to expand/prolong the time window (start & end) to cover other time zones too.
    //
    // NOTE: you might want to consider changing UI so that
    //   1) location is always asked first before date range
    //   2) use some 3rd party service to convert location to time zone (IANA tz name)
    //   3) Make exact dates filtering against that specific time zone
    //   This setup would be better for dates filter,
    //   but it enforces a UX where location is always asked first and therefore configurability
    const getProlongedStart = date => subtractTime(date, 14, 'hours', searchTZ);
    const getProlongedEnd = date => addTime(date, 12, 'hours', searchTZ);

    const startDate = hasValues ? parseDateFromISO8601(values[0], searchTZ) : null;
    const endRaw = hasValues ? parseDateFromISO8601(values[1], searchTZ) : null;
    const endDate =
      hasValues && isNightlyMode
        ? endRaw
        : hasValues
        ? getExclusiveEndDate(endRaw, searchTZ)
        : null;

    const today = getStartOf(new Date(), 'day', searchTZ);
    const possibleStartDate = subtractTime(today, 14, 'hours', searchTZ);
    const hasValidDates =
      hasValues &&
      startDate.getTime() >= possibleStartDate.getTime() &&
      startDate.getTime() <= endDate.getTime();

    // Whole UTC days: both dates are UTC midnights. `daysBetween` diffs in the browser's own time
    // zone, and a range across the end of summer time (one 25-hour day in Switzerland) came out
    // one day short — "Oct 21–25" asked for 4 days of availability instead of 5.
    const DAY_MS = 24 * 60 * 60 * 1000;
    const dayCount =
      isEntireRangeAvailable && startDate && endDate
        ? Math.round((endDate.getTime() - startDate.getTime()) / DAY_MS)
        : 1;
    const day = 1440;
    const hour = 60;
    // When entire range is required to be available, we count minutes of included date range,
    // but there's a need to subtract one hour due to possibility of daylight saving time.
    // If partial range is needed, then we just make sure that the shortest time unit supported
    // is available within the range.
    // You might want to customize this to match with your time units (e.g. day: 1440 - 60)
    //
    // Unified search: always one hour. The hosted filter is `time-full`, which asked for whole
    // days (− 1 h) and so removed every hourly meeting room: a single weekday in Zürich gave 23
    // results, with `minDuration` 60 it gives 47 and all 22 hourly rooms come back (measured
    // 2026-10-18). A day pass is still only found when it has a free day in the range — its
    // availability unit is a whole day.
    const minDuration = isUnifiedSearchEnabled()
      ? hour
      : isEntireRangeAvailable
      ? dayCount * day - hour
      : hour;
    return hasValidDates
      ? {
          start: getProlongedStart(startDate),
          end: getProlongedEnd(endDate),
          // Availability can be time-full or time-partial.
          // However, due to prolonged time window, we need to use time-partial.
          availability: 'time-partial',
          // minDuration uses minutes
          minDuration,
        }
      : {};
  };

  const stockFilters = datesMaybe => {
    const hasDatesFilterInUse = Object.keys(datesMaybe).length > 0;

    // If dates filter is not in use,
    //   1) Add minStock filter with default value (1)
    //   2) Add relaxed stockMode: "match-undefined"
    // The latter is used to filter out all the listings that explicitly are out of stock,
    // but keeps bookable and inquiry listings.
    return hasDatesFilterInUse ? {} : { minStock: 1, stockMode: 'match-undefined' };
  };

  const seatsSearchParams = (seats, datesMaybe) => {
    const seatsFilter = config.search.defaultFilters.find(f => f.key === 'seats');
    const hasDatesFilterInUse = Object.keys(datesMaybe).length > 0;

    // Honest places (unified search, util/searchPanelHonesty.js): `seats` never goes to the API
    // as availability seats — those count concurrent bookings, not persons (31 of 33 hourly
    // rooms have 1). It only means free monthly desks, see placesStockParams.
    if (isHonestPlacesActive()) {
      return {};
    }

    // Seats filter cannot be applied without dates
    return hasDatesFilterInUse && seatsFilter ? { seats } : {};
  };

  /**
   * "Freie Plätze" → `minStock` (unified search with HONEST_PLACES). Only when the listing-type
   * selection is exactly the monthly booking type (fixdesk / cowork24-abo): their stock is public
   * and is the number of free desks — fixdesk `minStock` 1 / 2 / 5 in the Zürich box → 24 / 17 / 12
   * (measured 2026-10-18). Any other selection ignores `seats`.
   *
   * `stockMode: 'strict'` (the API default, sent explicitly): a listing without stock does not
   * match a minStock query, so nothing slips through without a known number of free places.
   * The page-wide default is `match-undefined`, which keeps bookable listings without stock.
   *
   * Not combined with dates: the template never sends minStock with an availability query, and
   * monthly desks have no calendar — dates already remove all of them (the panel says so).
   */
  const placesStockParams = (seats, datesMaybe) => {
    const hasDatesFilterInUse = Object.keys(datesMaybe).length > 0;
    const count = placesCount(seats);
    const applies =
      !hasDatesFilterInUse &&
      count != null &&
      placesApply({
        urlQueryParams: searchParams,
        listingTypes: config.listing.listingTypes,
        listingTypePathParam: isListingTypeVariant ? listingTypePathParam : undefined,
      });
    return applies ? { minStock: count, stockMode: 'strict' } : {};
  };

  const sortSearchParams = (sortParam, hasKeywords) => {
    const sortConfig = config?.search?.sortConfig || {};
    // If no sort options are set, defaultSort will be undefined
    const defaultSort = sortConfig?.options?.[0]?.key;
    const relevanceEnabled = sortConfig.options?.some(
      option => option.key === sortConfig.relevanceKey
    );

    // User-specified sort takes priority
    if (sortParam !== undefined && sortParam !== sortConfig.relevanceKey) {
      return { sort: sortParam };
    }

    // No sort parameter needed when keyword search is used or sort config is inactive
    if (relevanceEnabled && (hasKeywords || !sortConfig.active)) {
      return {};
    }

    // Fall back to default sort
    return { sort: defaultSort };
  };

  const {
    perPage,
    price,
    dates,
    seats,
    sort,
    mapSearch,
    listingTypePathParam,
    isListingTypeVariant,
    ...restOfParams
  } = searchParams;
  // Lounge-category searches: ignore dates/seats entirely. The imported
  // lounges carry no Sharetribe availability plan, so an availability query
  // (start/end/minDuration) zero-results the whole category — the visit date
  // belongs to the external booking flow. This also covers URL-borne params
  // from shared/indexed/ad links; the filter UI is hidden separately in
  // SearchPageWithMap/SearchPageWithGrid. Regex match, never a hardcoded id.
  const isLoungeSearch = LOUNGE_MATCHER.test(`${searchParams?.pub_categoryLevel1 || ''}`);

  // The params related to default filters are prepared one-by-one
  // We could consider moving them to the prepareAPIParams function too.
  const priceMaybe = priceSearchParams(price);
  const datesMaybe = isLoungeSearch ? {} : datesSearchParams(dates);
  const stockMaybe = stockFilters(datesMaybe);
  // Lounge-Unterdrueckung bleibt: importierte Lounges haben keine Availability-Plans,
  // ein seats-Param zero-resultet sie komplett (siehe LOUNGE_MATCHER weiter oben).
  const seatsMaybe = isLoungeSearch ? {} : seatsSearchParams(seats, datesMaybe);
  // Overrides the default `minStock: 1` of stockMaybe when free places are asked for.
  const placesMaybe = isLoungeSearch ? {} : placesStockParams(seats, datesMaybe);
  // v10.15.0: sortSearchParams ersetzt die eigene relevanceKey-Abfrage und kann zusaetzlich
  // nach eigenen Zahlenfeldern sortieren.
  const sortMaybe = sortSearchParams(sort, searchParams?.keywords !== undefined);

  // Filter out potential referral data parameters so that they are not included in the API query
  const { userTypes = [] } = config.user;
  const validReferralSources = getReferralParams(userTypes);
  const apiParamsRaw = Object.fromEntries(
    Object.entries(restOfParams).filter(entry => {
      const [key, value] = entry;

      return !validReferralSources.includes(key);
    })
  );

  const params = {
    // The params that are related to listing fields and categories are prepared here.
    // We add handler functions that check category and integer range configurations.
    // - With category params, we essentially just omit invalid category names.
    //   I.e. params that are not part of the currently configured category tree.
    // - With integer range params, we prepare the property for API.
    //   I.e. the range end must be exclusive. E.g. 1000,2000 -> 1000,2001
    // Note: invalid independent search params are still passed through
    ...prepareAPIParams(apiParamsRaw, [prepareCategoryParams, prepareIntegerRangeParam]),
    // If the search page variant is of type /s/:listingType, this sets the pub_listingType
    // query parameter to the value of the listing type path parameter. The ordering matters here,
    // since this value overrides any possible pub_listingType value coming from query parameters
    // i.e. the previous row.
    //
    // Only one value is currently supported in pub_listingType – if you want to support e.g.
    // /s/:listingType?pub_listingType=[otherListingType] => pub_listingType=listingType,otherListingType,
    // you'll need to customize a logic that merges the query param and path param values.
    ...searchValidListingTypes(
      config.listing.listingTypes,
      listingTypePathParam,
      isListingTypeVariant
    ),
    ...priceMaybe,
    ...datesMaybe,
    ...stockMaybe,
    ...seatsMaybe,
    ...placesMaybe,
    ...sortMaybe,
    perPage,
  };

  return params;
};

/**
 * The companion query of a date search (util/calendarFreeSearch.js): the same search without
 * the dates, limited to the listing types that have no calendar — the availability filter of the
 * main query cannot see them. Null when the main query carries no availability filter (no date,
 * or a lounge search, which drops the dates up front) or nothing calendar-free is selected.
 */
const companionQueryParams = (searchParams, mainParams, config) => {
  if (!mainParams.start) {
    return null;
  }
  const companion = calendarFreeSearchParams(searchParams, config?.listing?.listingTypes);
  return companion ? buildListingsQueryParams(companion, config) : null;
};

/**
 * One response out of two: the dated matches first, then the calendar-free offers, each id once.
 * `meta` stays the main query's (`paginationUnsupported` for a date search) plus `extraItems`,
 * the number of appended offers — the page needs it to tell "24 of 24 dated, maybe more" from
 * "20 dated + 4 appended".
 */
const mergeResponses = (main, extra) => {
  if (!extra) {
    return main;
  }
  const seen = new Set((main?.data?.data || []).map(l => `${l.id?.uuid || l.id}`));
  const appended = (extra?.data?.data || []).filter(l => !seen.has(`${l.id?.uuid || l.id}`));
  return {
    ...main,
    data: {
      ...main.data,
      data: [...(main?.data?.data || []), ...appended],
      meta: { ...(main?.data?.meta || {}), extraItems: appended.length },
    },
  };
};

const searchListingsPayloadCreator = ({ searchParams, config }, thunkAPI) => {
  const { dispatch, rejectWithValue, extra: sdk } = thunkAPI;
  const params = buildListingsQueryParams(searchParams, config);
  const companionParams = companionQueryParams(searchParams, params, config);
  const listingFields = config?.listing?.listingFields;
  const sanitizeConfig = { listingFields };

  return Promise.all([
    sdk.listings.query(params),
    companionParams ? sdk.listings.query(companionParams) : Promise.resolve(null),
  ])
    .then(([mainResponse, extraResponse]) => {
      dispatch(addMarketplaceEntities(mainResponse, sanitizeConfig));
      if (extraResponse) {
        dispatch(addMarketplaceEntities(extraResponse, sanitizeConfig));
      }
      const response = mergeResponses(mainResponse, extraResponse);

      // Only track the first page of a query as a `search` event to avoid
      // counting pagination as separate searches.
      const isFirstPage = !searchParams?.page || `${searchParams.page}` === '1';
      if (isFirstPage) {
        track.search({
          search_term: searchParams?.keywords,
          address: searchParams?.address,
          results: response?.data?.data?.length,
        });
      }

      return response;
    })
    .catch(e => {
      const error = storableError(e);
      if (!(isErrorUserPendingApproval(error) || isForbiddenError(error))) {
        return rejectWithValue(error);
      }
      return rejectWithValue(error);
    });
};

export const searchListings = createAsyncThunk(
  'SearchPage/searchListings',
  searchListingsPayloadCreator
);

// ================ Slice ================ //

const searchPageSlice = createSlice({
  name: 'SearchPage',
  initialState: {
    pagination: null,
    searchParams: null,
    searchInProgress: false,
    searchListingsError: null,
    currentPageResultIds: [],
    activeListingId: null,
  },
  reducers: {
    setActiveListing: (state, action) => {
      state.activeListingId = action.payload;
    },
  },
  extraReducers: builder => {
    // Search Listings
    builder
      .addCase(searchListings.pending, (state, action) => {
        state.searchParams = action.meta.arg.searchParams;
        state.searchInProgress = true;
        state.searchListingsError = null;
      })
      .addCase(searchListings.fulfilled, (state, action) => {
        state.currentPageResultIds = resultIds(action.payload.data);
        state.pagination = action.payload.data.meta;
        state.searchInProgress = false;
      })
      .addCase(searchListings.rejected, (state, action) => {
        console.error(action.payload);
        state.searchInProgress = false;
        state.searchListingsError = action.payload;
      });
  },
});

// Export the action creator
export const { setActiveListing } = searchPageSlice.actions;

export default searchPageSlice.reducer;

// ================ Load data ================ //

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  // In private marketplace mode, this page won't fetch data if the user is unauthorized
  const { listingType: listingTypePathParam } = params || {};
  const state = getState();
  const currentUser = state.user?.currentUser;
  const isAuthorized = currentUser && isUserAuthorized(currentUser);
  const hasViewingRights = currentUser && hasPermissionToViewData(currentUser);
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const canFetchData =
    !isPrivateMarketplace || (isPrivateMarketplace && isAuthorized && hasViewingRights);
  if (!canFetchData) {
    return Promise.resolve();
  }

  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  // `rest` is forwarded to the Marketplace API more or less as-is (see `prepareAPIParams` below,
  // whose comment notes that unrecognised params are passed through on purpose). The Topbar's
  // overlay params share the same query string but are not search input, so they are dropped
  // before they can get that far — verified 2026-08-07: opening the login dialog on /s used to
  // send `authmodal=open` along to listings/query. Routes.js no longer re-runs loadData for an
  // overlay toggle at all; this stays as the guarantee that no other caller can leak them either.
  const { page = 1, address, origin, ...rest } = omitUiOnlyParams(queryParams);
  const originMaybe = isOriginInUse(config) && origin ? { origin } : {};

  const listingTypeVariantMaybe = listingTypePathParam
    ? { listingTypePathParam, isListingTypeVariant: true }
    : {};

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const searchListingsCall = searchListings({
    searchParams: {
      ...rest,
      ...originMaybe,
      ...listingTypeVariantMaybe,
      page,
      perPage: RESULT_PAGE_SIZE,
      include: ['author', 'images'],
      'fields.listing': [
        'title',
        'geolocation',
        'price',
        'deleted',
        'state',
        'publicData.listingType',
        'publicData.transactionProcessAlias',
        'publicData.unitType',
        'publicData.cardStyle',
        // These help rendering of 'purchase' listings,
        // when transitioning from search page to listing page
        'publicData.pickupEnabled',
        'publicData.shippingEnabled',
        'publicData.priceVariationsEnabled',
        'publicData.priceVariants',
      ],
      // Beide Extended-Data-Felder des Nutzers MUESSEN hier stehen, in jedem
      // Sparse-Fieldset, das Nutzer laedt — nicht nur dort, wo etwas davon sichtbar
      // ist: combinedResourceObjects (util/data.js) ersetzt attributes.profile beim
      // Zusammenfuehren KOMPLETT, und sanitizeUser erzwingt publicData:{} als
      // eigenen Schluessel (Destructuring-Default). Eine sparsame Antwort loescht
      // also die Extended Data einer bereits geladenen vollen Antwort aus dem Store.
      // publicData traegt stripeConnected — die Weiche zwischen Standard-Checkout
      // und Soft-Booking (util/softReservation.js) —, metadata traegt stripeVerified
      // fuer das Badge. Fehlt publicData, landet ein zahlungsbereiter Anbieter im
      // langsameren Soft-Booking.
      'fields.user': [
        'profile.displayName',
        'profile.abbreviatedName',
        'profile.publicData',
        'profile.metadata',
      ],
      'fields.image': [
        'variants.scaled-small',
        'variants.scaled-medium',
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      // The result cards carry a photo slider (ListingCardImageSlider), so the search
      // needs more than the cover image. Kept at MAX_SLIDER_IMAGES — every extra
      // image is four variant URLs in the SSR payload, and only the first is
      // fetched before the visitor swipes.
      'limit.images': MAX_SLIDER_IMAGES,
    },
    config,
  });

  return dispatch(searchListingsCall);
};

// ================ Result count ================ //

// A date search (availability filter) gets no totalItems from the API — `paginationUnsupported`,
// the same case the result list handles with `listings.length`. The count then comes from one
// page of this size, and "100+" stands for "at least that many".
const COUNT_PAGE_SIZE_WITH_DATES = 100;

/**
 * How many listings a set of not-yet-applied filters would return — the number on the filter
 * dialog's "N Angebote anzeigen" button. Same param preparation as the result list (via
 * buildListingsQueryParams), so the number matches what the list shows after applying. Titles
 * only: the count lives in `meta`, the rest of the response is never used.
 *
 * Nothing goes into the store: the dialog keeps the number for itself, and a count for filters
 * the visitor never applied has no business in the page's pagination.
 *
 * @param {Object} queryParams search params in URL shape (address, bounds, price, pub_* …)
 * @param {Object} pathParams route params; `listingType` pins the type on /s/:listingType
 * @param {Object} config the app config
 * @returns {Promise<{count: number, isLowerBound: boolean}|null>} null when the API refused or failed
 */
export const countListings = (queryParams, pathParams, config) => (dispatch, getState, sdk) => {
  const { listingType: listingTypePathParam } = pathParams || {};
  const { page, address, origin, ...rest } = omitUiOnlyParams(queryParams || {});
  // Staged values that were cleared are null — a cleared filter is no filter.
  const filterParams = Object.fromEntries(
    Object.entries(rest).filter(([, value]) => value != null && value !== '')
  );
  const originMaybe = isOriginInUse(config) && origin ? { origin } : {};
  const listingTypeVariantMaybe = listingTypePathParam
    ? { listingTypePathParam, isListingTypeVariant: true }
    : {};

  const params = buildListingsQueryParams(
    {
      ...filterParams,
      ...originMaybe,
      ...listingTypeVariantMaybe,
      perPage: 1,
      'fields.listing': ['title'],
    },
    config
  );
  // `start` is only there when the dates filter is in use (seats imply dates).
  const isDateSearch = !!params.start;
  const perPage = isDateSearch ? COUNT_PAGE_SIZE_WITH_DATES : 1;
  // A date search also shows the calendar-free offers (companion query of searchListings); the
  // count adds them, and the API totals that query as usual.
  const companionParams = companionQueryParams(
    { ...filterParams, ...originMaybe, ...listingTypeVariantMaybe },
    params,
    config
  );

  const countOf = (response, pageSize) => {
    const { meta = {}, data = [] } = response?.data || {};
    return meta.totalItems != null
      ? { count: meta.totalItems, isLowerBound: false }
      : { count: data.length, isLowerBound: data.length >= pageSize };
  };

  return Promise.all([
    sdk.listings.query({ ...params, perPage }),
    companionParams
      ? sdk.listings.query({ ...companionParams, 'fields.listing': ['title'], perPage: 1 })
      : Promise.resolve(null),
  ])
    .then(([mainResponse, extraResponse]) => {
      const main = countOf(mainResponse, perPage);
      const extra = extraResponse ? countOf(extraResponse, 1) : { count: 0, isLowerBound: false };
      return { count: main.count + extra.count, isLowerBound: main.isLowerBound };
    })
    .catch(() => null);
};
